import { apiClient } from './client';

// Mirrors packages/api/src/routes/outreach.ts (the web app keeps its own copy
// of response shapes, as outbound.ts and plays.ts do).

export type PlayId = string;

export type CandidateStatus = 'proposed' | 'confirmed' | 'enrolled' | 'blocked' | 'dismissed';

export const CANDIDATE_STATUSES: readonly CandidateStatus[] = [
  'proposed',
  'confirmed',
  'enrolled',
  'blocked',
  'dismissed',
];

/** `weeks` query value: a number of Brussels weeks, or the whole history. */
export type OutreachWeeks = number | 'all';

// GET /api/outreach/overview?weeks=12|all
export interface OutreachOverview {
  from: string; to: string;               // ISO window used for activity counts
  lemlistLastSyncAt: string | null;       // newest successful pull, daily or hourly run
  lemlistLastSyncStatus: string | null;   // the daily integration sync's status (the hourly run never writes it)
  lemlistSyncFailure: SyncFailure | null; // latest daily run failed (else latest hourly run failed); null when both are fine
  signals: SignalOverviewRow[];           // 7, register order
  campaigns: CampaignOverviewRow[];       // non-signal running/paused
  /** Reply messages in the window across every campaign, bridged to the rows' "replied" people. Absent on an older API. */
  replyBridge?: ReplyBridge;
}

/** messages = listedPeople + repeatMessages + otherCampaignMessages + botMessages. */
export interface ReplyBridge {
  messages: number;              // every reply message lemlist recorded in the window (the Outbound "Replies" tile)
  listedPeople: number;          // distinct people who replied, per signal/campaign row, summed over the rows
  repeatMessages: number;        // further replies from those same people
  otherCampaignMessages: number; // replies in campaigns not listed (finished, archived, or no campaign)
  botMessages: number;           // replies lemlist flagged as bot activity in listed campaigns
}

/** The latest lemlist sync run that failed, when it is the newest run of its kind. */
export interface SyncFailure { run: 'daily' | 'hourly'; at: string; error: string | null }

export interface FunnelCounts {
  contacted: number;     // distinct leads, effort types
  invited: number;       // distinct leads sent a LinkedIn invite in the period
  connected: number;     // of `invited`, how many have accepted (any time) — same cohort as Outbound activity
  connectRate: number | null;      // connected / invited
  replied: number;       // distinct leads, REPLY_TYPES
  interested: number;    // distinct leads marked interested (POSITIVE_REPLY_TYPES)
  notInterested: number; // distinct leads, NEGATIVE_REPLY_TYPES
  failed: number;        // rows, FAILURE_TYPES — pending-invitation messages excluded
  messagesBeforeAccept: number;    // rows, messages fired before the invite was accepted (not a failure)
  touches: number;       // rows, effort types
  replyRate: number | null;        // replied / contacted
  interestedRate: number | null;   // interested / contacted
  lastActivityAt: string | null;
}

export interface CampaignRef { id: string; name: string; status: string | null; senders: { userId: string; name: string | null }[] }

export interface CampaignOverviewRow {
  campaign: CampaignRef;
  leadsInCampaign: number;
  rep: string | null;           // name of the lemlist user (by sendUserId) with most effort rows
  funnel: FunnelCounts;
}

export interface SignalOverviewRow extends Omit<CampaignOverviewRow, 'campaign' | 'rep'> {
  playId: PlayId;
  label: string;
  campaignPrefix: string;       // lemlist campaign-name prefix the play resolves on, e.g. "[SIGNAL 04A]"
  campaign: CampaignRef | null;
  ambiguous: string[];
  detectorGap: string | null;
  detected: number | null;      // null when no detector
  candidates: Record<CandidateStatus, number>;
}

// GET /api/outreach/signals/:playId?weeks=
// GET /api/outreach/campaigns/:campaignId?weeks=   (same shape, signal fields null)
export interface OutreachDetail {
  from: string; to: string;
  lemlistLastSyncAt: string | null;
  lemlistSyncFailure: SyncFailure | null;
  signal: { playId: PlayId; label: string; rationale: string; campaignPrefix: string; detectorGap: string | null; detected: number | null; candidates: Record<CandidateStatus, number> } | null;
  campaign: CampaignRef | null;
  leadsInCampaign: number;
  funnel: FunnelCounts;
  steps: StepRow[];                             // sequence tree, index order
  replies: ReplyRow[];                          // newest 50
  weekly: { week: string; contacted: number; invited: number; connected: number; replied: number; interested: number; failed: number }[];
  blocked: { reason: string; count: number; rows: BlockedRow[] }[];   // signal only
  lemlistSays: LemlistSaysStats | null; // live stats call, null on failure with `lemlistSaysError`
  lemlistSaysError: string | null;
}

/** lemlist's own campaign stats. A field lemlist did not return is null, never 0. */
export interface LemlistSaysStats {
  nbLeadsReached: number | null;
  nbLeadsAnswered: number | null;
  nbLeadsInterested: number | null;
  invitationAccepted: number | null;
  messagesNotSent: number | null;
}

export interface StepRow {
  stepId: string; sequenceId: string; level: number; index: number; sequenceStep: number;
  type: string;                    // linkedinVisit | linkedinInvite | linkedinSend | email | conditional | manual | …
  delayDays: number;
  branchLabel: string | null;      // "Is in Linkedin network (1st)" | "fallback" | null for root
  branchTaken: number | null;      // conditionChosen rows that routed here
  copy: { message: string | null; altMessage: string | null; altMessagePremium: string | null; variables: string[] } | null;
  renderedSamples: { leadId: string; leadFirstName: string | null; sentAt: string; message: string }[]; // last 3
  sent: number; failed: number; skipped: number; opened: number; accepted: number;
  replies: number; interested: number; notInterested: number;
  replyRate: number | null;
  failureReasons: { message: string; count: number }[];
}

export interface ReplyRow {
  activityId: string; occurredAt: string; type: string;
  leadId: string; leadName: string | null; leadCompanyName: string | null; leadLinkedinUrl: string | null;
  stepId: string | null; stepIndex: number | null;
  text: string | null; subject: string | null;
  interestScore: number | null;
  verdict: 'interested' | 'not_interested' | null;   // from a later linkedinInterested/NotInterested on the same lead
  candidateId: string | null;                       // when the lead came through a play
}

export interface BlockedRow { candidateId: string; companyName: string; personName: string | null; status: 'blocked' | 'dismissed'; reason: string | null; decidedBy: string | null; decidedAt: string | null }

export async function getOutreachOverview(weeks: OutreachWeeks): Promise<OutreachOverview> {
  return apiClient.get<OutreachOverview>(`/outreach/overview?weeks=${weeks}`);
}

export async function getOutreachSignal(playId: PlayId, weeks: OutreachWeeks): Promise<OutreachDetail> {
  return apiClient.get<OutreachDetail>(`/outreach/signals/${encodeURIComponent(playId)}?weeks=${weeks}`);
}

export async function getOutreachCampaign(campaignId: string, weeks: OutreachWeeks): Promise<OutreachDetail> {
  return apiClient.get<OutreachDetail>(`/outreach/campaigns/${encodeURIComponent(campaignId)}?weeks=${weeks}`);
}

// GET /api/outreach/lead-companies/unknown
export interface LeadCompanyCoverage {
  people: number;                 // distinct people in live lemlist campaigns
  bySource: Record<'lemlist' | 'attio' | 'apollo' | 'manual', number>;
  unresolved: number;
  pending: number;                // not looked up yet (Apollo budget per hour, or a failed run)
  lastWriteErrors: number;        // people whose last write-back to lemlist was refused
}

export interface UnknownLeadCompany {
  id: string;
  fullName: string | null;
  jobTitle: string | null;
  linkedinUrl: string | null;
  email: string | null;
  reason: string | null;
  campaigns: { id: string; name: string | null }[];
  checkedAt: string;
}

export interface UnknownLeadCompanies { coverage: LeadCompanyCoverage; unknown: UnknownLeadCompany[] }

export async function getUnknownLeadCompanies(): Promise<UnknownLeadCompanies> {
  return apiClient.get<UnknownLeadCompanies>('/outreach/lead-companies/unknown');
}

// POST /api/outreach/lead-companies/:id
export async function nameLeadCompany(
  id: string,
  body: { companyName: string; companyDomain?: string | null },
): Promise<{ leadsWrittenToLemlist: number; writeErrors: string[] }> {
  return apiClient.post(`/outreach/lead-companies/${encodeURIComponent(id)}`, body);
}
